import classnames from 'classnames/bind'
import { FormProvider } from 'react-hook-form'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import { JUDGEME, TRACKING_EVENTS } from '~/lib/constants'

import { useGetCustomer } from '@unlikelystudio/react-ecommerce-hooks'

import Input from '~/components/Form/Input'
import BlurredCta from '~/components/UI/BlurredCta'
import { StarEmptyIcon, StarFullIcon } from '~/components/UI/Icons'

import RecaptchaHandler from '~/providers/RecaptchaProvider/RecaptchaHandler'
import { useStyle } from '~/providers/StyleProvider'
import { useTracker } from '~/providers/TrackerProvider'

import useCustomerErrors from '~/hooks/account/useCustomerErrors'
import useAlerts from '~/hooks/useAlerts'
import useFormWithMutation from '~/hooks/useFormWithMutation'
import useLocale from '~/hooks/useLocale'
import usePostReview from '~/hooks/usePostReview'
import useTextColorFromTheme from '~/hooks/useTextColorFromTheme'

import { CTA, FORM, REVIEWS } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

const DEFAULT_RATING = 5

export interface ReviewFormProps {
  className?: string
  productId?: string
  productLink?: string
  hasReviewed?: boolean
  onReviewSent?(): void
}

function ReviewForm({
  className,
  productId,
  productLink,
  hasReviewed,
  onReviewSent,
}: ReviewFormProps) {
  const { textColor: color } = useTextColorFromTheme()
  const t = useTranslate()
  const onError = useCustomerErrors()
  const triggerAlert = useAlerts()
  const tracker = useTracker()

  const triggerEvent = () =>
    tracker.emit(TRACKING_EVENTS.REVIEW_SUBMITTED, { formName: 'review_form' })

  const handleRegister = (data) => {
    mutate(data)
  }
  const { form, onSubmit } = useFormWithMutation(
    handleRegister,
    null,
    'post_review',
  )

  const textStyle = useStyle({
    textPreset: GlobalTextPreset.Text12Secondary,
    color,
  })

  const locale = useLocale()

  const { data } = useGetCustomer({
    onSuccess: (user) => {
      form.reset({
        name: `${user.firstName} ${user.lastName}`,
        email: user?.email ?? '',
      })
    },
  })

  const currentNote = form.watch('rating')
  const currentBody = form.watch('body')

  const { mutate, isLoading } = usePostReview({
    onSuccess: () => {
      onReviewSent?.()
      triggerEvent()
      triggerAlert('REVIEW_SUBMITTED')
    },
    onError: (error) => {
      const { errsGlobals, errsFields } = onError(error, form.setError)
      const errors = Object.values(errsGlobals)

      if (!errors?.length && !Object.values(errsFields)?.length) {
        triggerAlert()
      }
    },
  })

  const labelStyle = useStyle({
    textPreset: GlobalTextPreset.Cta12PrimaryReg,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const inputTextStyle = useStyle({
    textPreset: GlobalTextPreset.Text12Secondary,
  })

  return (
    <div className={cx(css.ReviewForm, className)}>
      {!hasReviewed && (
        <RecaptchaHandler>
          <FormProvider {...form}>
            <form className={cx(css.form, className)} onSubmit={onSubmit}>
              <div className={cx(css.ratingsRow)}>
                <div className={cx(css.ratings)}>
                  {Array(JUDGEME.MAX_RATING)
                    .fill(null)
                    ?.map((_, i) => (
                      <label
                        className={cx(css.rating)}
                        key={`rating_input_${i + 1}`}
                        htmlFor={`rating-${i + 1}`}>
                        <Input
                          className={cx(css.input)}
                          id={`rating-${i + 1}`}
                          value={i + 1}
                          defaultChecked={i + 1 === DEFAULT_RATING}
                          preset={'starRating'}
                        />
                        {(currentNote ?? DEFAULT_RATING) >= i + 1 ? (
                          <StarFullIcon className={cx(css.star)} />
                        ) : (
                          <StarEmptyIcon className={cx(css.star)} />
                        )}
                      </label>
                    ))}
                </div>
                <span className={cx(labelStyle)}>{t(REVIEWS.NOTE)}</span>
              </div>
              <Input
                className={cx(css.input, css.fullName, inputTextStyle)}
                preset={'fullName'}
                disabled={hasReviewed}
                theme={color}
              />
              <Input
                className={cx(css.input, css.email, inputTextStyle)}
                preset={'email'}
                disabled={hasReviewed}
                theme={color}
              />

              <Input
                className={cx(css.input, css.reviewTitle, inputTextStyle)}
                preset={'reviewTitle'}
                disabled={hasReviewed}
                theme={color}
              />
              <div
                className={cx(css.inputContainer, css.input, css.reviewBody)}>
                <Input
                  name="body"
                  textarea
                  required
                  value={currentBody}
                  placeholder={t(FORM.REVIEW_BODY)}
                  theme={color}
                  disabled={hasReviewed}
                  className={cx(inputTextStyle, css.textarea)}
                  inputClassName={cx(css.textAreaInnerInput)}
                />
              </div>
              <Input
                className={cx(css.input, inputTextStyle)}
                defaultValue={productId}
                type="hidden"
                name="id"
              />
              <div className={cx(css.bottomRow)}>
                <BlurredCta
                  className={cx(css.submit)}
                  onClick={onSubmit}
                  disabled={isLoading}
                  theme={GlobalThemeColors.Black}
                  isBig
                  isLoading={isLoading}>
                  {t(FORM.SAVE)}
                </BlurredCta>
              </div>
            </form>
          </FormProvider>
        </RecaptchaHandler>
      )}
      {hasReviewed && (
        <div className={cx(css.reviewSent, textStyle)}>
          <BlurredCta
            className={cx(css.homeLink)}
            theme={GlobalThemeColors.Black}
            isBig
            href={productLink}>
            {t(CTA.SEE_PRODUCT)}
          </BlurredCta>
        </div>
      )}
    </div>
  )
}

ReviewForm.defaultProps = {}

export default ReviewForm
